import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Button, 
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import SecurityIcon from '@mui/icons-material/Security';
import WarningIcon from '@mui/icons-material/Warning';
import clientsApi from '../../api/clientsApi';
import PaginatedTable from '../../components/PaginatedTable';
import SortableTableHeader from '../../components/SortableTableHeader';
import { useSecureDocuments } from '../../hooks/useSecureDocuments';
import DocumentViewerModal from '../../components/DocumentViewerModal';

const ClientDocuments = ({ clientId, client }) => {
  const navigate = useNavigate();
  // Client data is passed as prop, no need for state
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [sortField, setSortField] = useState('created_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [viewingStates, setViewingStates] = useState({});
  const [downloadingStates, setDownloadingStates] = useState({});
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  // Use the new secure documents hook
  const { 
    loading: secureLoading, 
    error: secureError, 
    openSecureDocument, 
    downloadSecureDocument,
    getLegacyDocumentUrl 
  } = useSecureDocuments();

  const fetchData = async () => {
    setLoading(true);
    try {
      const documentsData = await clientsApi.getClientDocuments(clientId, {
        skip: page * pageSize,
        limit: pageSize,
        order_by: sortField,
        order_direction: sortDirection
      });
      setDocuments(documentsData.documents || []);
      setTotalDocuments(documentsData.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching client documents:', err);
      setError('Failed to load client documents. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchData();
    }
  }, [clientId, page, pageSize, sortField, sortDirection]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(0);
  };

  const handleBack = () => {
    // This component is embedded in the client detail page
  };

  const handleOpenDocument = async (doc, useSecure = true) => {
    if (useSecure) {
      // Use new in-app viewer for secure documents
      setSelectedDocument(doc);
      setViewerOpen(true);
    } else {
      // Fallback to legacy access (deprecated) - still use popup
      setViewingStates(prev => ({ ...prev, [doc.id]: true }));
      try {
        const urlData = await getLegacyDocumentUrl(doc.id);
        window.open(urlData.url, '_blank', 'noopener,noreferrer');
      } catch (err) {
        setError(`Could not access this document: ${err.message}`);
        console.error('Error accessing document:', err);
      } finally {
        setViewingStates(prev => ({ ...prev, [doc.id]: false }));
      }
    }
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setSelectedDocument(null);
  };

  const handleDownloadDocument = async (docId, filename) => {
    setDownloadingStates(prev => ({ ...prev, [docId]: true }));
    try {
      await downloadSecureDocument(docId, filename);
    } catch (err) {
      setError(`Could not download this document: ${err.message}`);
      console.error('Error downloading document:', err);
    } finally {
      setDownloadingStates(prev => ({ ...prev, [docId]: false }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString();
  };

  const getFileExtension = (fileName) => {
    if (!fileName) return '';
    return fileName.split('.').pop()?.toUpperCase() || '';
  };
  
  const columns = [
    { id: 'file_name', label: 'File Name', sortable: true },
    { id: 'created_date', label: 'Created', sortable: true },
    { id: 'security', label: 'Security' },
    { id: 'actions', label: 'Actions' }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {secureError && <Alert severity="error" sx={{ mb: 2 }}>{secureError}</Alert>}

      <Alert severity="info" sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SecurityIcon sx={{ mr: 1 }} />
          <Typography variant="body2">
            All documents now use secure access with authentication and tenant isolation. 
            URLs expire after 5 minutes and cannot be shared.
          </Typography>
        </Box>
      </Alert>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <SortableTableHeader
                    key={column.id}
                    field={column.id}
                    label={column.label}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    sortable={column.sortable}
                  />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2">{doc.file_name || 'No filename'}</Typography>
                      {doc.source === 'pabau' && (
                        <Tooltip title="This document is currently sourced from Pabau. It will be synced to our system soon.">
                          <Chip label="P" size="small" sx={{ ml: 1, cursor: 'help', backgroundColor: '#ffc107', color: 'black' }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(doc.created_date)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {doc.gcs_path ? (
                        <Tooltip title="Secure access: Authentication required, tenant isolated, 5-minute expiry">
                          <Chip 
                            icon={<SecurityIcon />} 
                            label="Secure" 
                            size="small" 
                            color="success" 
                            sx={{ cursor: 'help' }} 
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Legacy access: Less secure, longer expiry, can be shared">
                          <Chip 
                            icon={<WarningIcon />} 
                            label="Legacy" 
                            size="small" 
                            color="warning" 
                            sx={{ cursor: 'help' }} 
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={doc.gcs_path ? "View with secure access" : "View with legacy access"}>
                        <IconButton 
                          onClick={() => handleOpenDocument(doc, !!doc.gcs_path)} 
                          disabled={viewingStates[doc.id]}
                          color={doc.gcs_path ? "primary" : "warning"}
                        >
                          {viewingStates[doc.id] ? <CircularProgress size={24} /> : <VisibilityIcon />}
                        </IconButton>
                      </Tooltip>
                      
                      {doc.gcs_path && (
                        <Tooltip title="Download with secure access">
                          <IconButton 
                            onClick={() => handleDownloadDocument(doc.id, doc.file_name)} 
                            disabled={downloadingStates[doc.id]}
                            color="primary"
                          >
                            {downloadingStates[doc.id] ? <CircularProgress size={24} /> : <DownloadIcon />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <PaginatedTable
          totalItems={totalDocuments}
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </Paper>

      <DocumentViewerModal
        open={viewerOpen}
        onClose={handleCloseViewer}
        documentId={selectedDocument?.id}
        documentName={selectedDocument?.file_name}
        documentInfo={selectedDocument}
      />
    </Box>
  );
};

export default ClientDocuments; 